import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { ChevronDown, Menu, X, Globe, ShoppingBag, Users, CalendarDays, Wallet, Sparkles, Map, LogIn, LogOut, User, Clapperboard } from "lucide-react";
import ismLogo from "@assets/IMG_2117_1779819410701.png";
import WalletPanel from "@/components/WalletPanel";
import { useWallet } from "@/hooks/useWallet";
import { useAuth } from "@/contexts/AuthContext";

function short(addr: string) {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

export default function Navbar() {
  const [marketOpen, setMarketOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [walletOpen, setWalletOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [location] = useLocation();
  const { address, isConnected } = useWallet();
  const { firebaseUser, userProfile, logout } = useAuth();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setMarketOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMarketOpen(false);
  }, [location]);

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b border-white/10 bg-background/90 backdrop-blur-md">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">

          {/* Logo */}
          <div className="flex items-center gap-6">
            <Link href="/" className="flex items-center gap-2 flex-shrink-0">
              <img src={ismLogo} alt="ISM Logo" className="h-8 w-8 object-contain" />
              <span className="font-bold text-lg tracking-widest uppercase">ISM | New World</span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex gap-6 text-sm font-medium items-center">
              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => setMarketOpen(o => !o)}
                  className="text-muted-foreground hover:text-white transition-colors flex items-center gap-1 focus:outline-none"
                  data-testid="nav-marketplace-trigger"
                >
                  <ShoppingBag className="w-3.5 h-3.5" />
                  Marketplace
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${marketOpen ? "rotate-180" : ""}`} />
                </button>

                {marketOpen && (
                  <div className="absolute top-full left-0 mt-2 w-48 bg-background/95 backdrop-blur-md border border-white/10 rounded-lg shadow-xl overflow-hidden z-50">
                    <Link href="/marketplace" className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-white hover:bg-white/5 transition-colors" data-testid="nav-marketplace">
                      <ShoppingBag className="w-4 h-4 text-primary" />
                      Marketplace
                    </Link>
                    <div className="border-t border-white/5" />
                    <Link href="/avatars" className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-white hover:bg-white/5 transition-colors" data-testid="nav-avatars">
                      <Users className="w-4 h-4 text-primary" />
                      Avatars
                    </Link>
                    <Link href="/events" className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-white hover:bg-white/5 transition-colors" data-testid="nav-events">
                      <CalendarDays className="w-4 h-4 text-primary" />
                      Events
                    </Link>
                  </div>
                )}
              </div>

              <Link href="/world-map" className="text-muted-foreground hover:text-white transition-colors flex items-center gap-1.5" data-testid="nav-world-map">
                <Map className="w-3.5 h-3.5" />
                World Map
              </Link>

              <Link href="/urban-film-festival-miami" className="text-muted-foreground hover:text-white transition-colors flex items-center gap-1.5" data-testid="nav-film-festival">
                <Clapperboard className="w-3.5 h-3.5" />
                Film Festival
              </Link>

              <Link href="/worlds" className="text-muted-foreground hover:text-white transition-colors flex items-center gap-1.5" data-testid="nav-worlds">
                <Globe className="w-3.5 h-3.5" />
                Worlds
              </Link>

              <Link href="/viewer" className="text-muted-foreground hover:text-white transition-colors" data-testid="nav-viewer">
                3D Viewer
              </Link>

              <Link href="/creators" className="text-muted-foreground hover:text-white transition-colors flex items-center gap-1.5" data-testid="nav-creators">
                <Sparkles className="w-3.5 h-3.5" />
                Creators
              </Link>
            </nav>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Wallet */}
            {isConnected && address ? (
              <Button
                variant="outline"
                className="hidden sm:flex items-center gap-2 border-green-500/40 text-green-400 hover:bg-green-500/10 font-mono text-xs"
                onClick={() => setWalletOpen(true)}
                data-testid="btn-connect-wallet"
              >
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                {short(address)}
              </Button>
            ) : (
              <Button
                variant="outline"
                className="hidden sm:flex items-center gap-2 border-primary/50 text-primary hover:bg-primary/20"
                onClick={() => setWalletOpen(true)}
                data-testid="btn-connect-wallet"
              >
                <Wallet className="w-4 h-4" />
                Connect Wallet
              </Button>
            )}

            {/* Auth */}
            {firebaseUser ? (
              <div ref={userMenuRef} className="relative">
                <button
                  onClick={() => setUserMenuOpen(o => !o)}
                  className="flex items-center gap-2 rounded-full focus:outline-none"
                  aria-label="User menu"
                >
                  {userProfile?.photoUrl || firebaseUser.photoURL ? (
                    <img
                      src={userProfile?.photoUrl ?? firebaseUser.photoURL ?? ""}
                      alt="Avatar"
                      className="w-8 h-8 rounded-full border border-white/20 object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                  )}
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-background/95 backdrop-blur-md border border-white/10 rounded-lg shadow-xl overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-white/10">
                      <p className="text-xs text-white font-medium truncate">
                        {userProfile?.displayName ?? firebaseUser.displayName ?? "Member"}
                      </p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {userProfile?.email ?? firebaseUser.email}
                      </p>
                    </div>
                    <button
                      onClick={() => { logout(); setUserMenuOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <LogOut className="w-4 h-4 text-red-400" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link href="/auth">
                <Button
                  variant="outline"
                  size="sm"
                  className="hidden sm:flex items-center gap-2 border-white/20 text-white hover:bg-white/5 text-xs"
                >
                  <LogIn className="w-3.5 h-3.5" />
                  Sign In
                </Button>
              </Link>
            )}

            <button
              className="md:hidden p-2 text-muted-foreground hover:text-white transition-colors"
              onClick={() => setMobileOpen(o => !o)}
              aria-label="Toggle menu"
              data-testid="btn-mobile-menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-white/10 bg-background/95 backdrop-blur-md">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-1">
              <Link href="/marketplace" className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-white hover:bg-white/5 transition-colors" data-testid="mobile-nav-marketplace">
                <ShoppingBag className="w-4 h-4 text-primary" />
                Marketplace
              </Link>
              <Link href="/avatars" className="flex items-center gap-3 px-3 py-3 pl-8 rounded-lg text-sm text-muted-foreground hover:text-white hover:bg-white/5 transition-colors" data-testid="mobile-nav-avatars">
                <Users className="w-4 h-4 text-primary/60" />
                Avatars
              </Link>
              <Link href="/events" className="flex items-center gap-3 px-3 py-3 pl-8 rounded-lg text-sm text-muted-foreground hover:text-white hover:bg-white/5 transition-colors" data-testid="mobile-nav-events">
                <CalendarDays className="w-4 h-4 text-primary/60" />
                Events
              </Link>
              <Link href="/world-map" className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-white hover:bg-white/5 transition-colors" data-testid="mobile-nav-world-map">
                <Map className="w-4 h-4 text-primary" />
                World Map
              </Link>
              <Link href="/urban-film-festival-miami" className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-white hover:bg-white/5 transition-colors" data-testid="mobile-nav-film-festival">
                <Clapperboard className="w-4 h-4 text-primary" />
                Urban Film Festival
              </Link>
              <Link href="/worlds" className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-white hover:bg-white/5 transition-colors" data-testid="mobile-nav-worlds">
                <Globe className="w-4 h-4 text-primary" />
                Worlds
              </Link>
              <Link href="/viewer" className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-white hover:bg-white/5 transition-colors" data-testid="mobile-nav-viewer">
                <span className="w-4 h-4 flex items-center justify-center text-primary text-xs">3D</span>
                3D Viewer
              </Link>
              <Link href="/creators" className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-muted-foreground hover:text-white hover:bg-white/5 transition-colors" data-testid="mobile-nav-creators">
                <Sparkles className="w-4 h-4 text-primary" />
                Creators
              </Link>

              <div className="pt-3 border-t border-white/10 mt-2 space-y-2">
                {isConnected && address ? (
                  <Button
                    className="w-full gap-2 border-green-500/40 text-green-400 hover:bg-green-500/10 font-mono text-xs"
                    variant="outline"
                    onClick={() => { setMobileOpen(false); setWalletOpen(true); }}
                    data-testid="mobile-btn-connect-wallet"
                  >
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    {short(address)}
                  </Button>
                ) : (
                  <Button
                    className="w-full gap-2 border-primary/50 text-primary hover:bg-primary/20"
                    variant="outline"
                    onClick={() => { setMobileOpen(false); setWalletOpen(true); }}
                    data-testid="mobile-btn-connect-wallet"
                  >
                    <Wallet className="w-4 h-4" />
                    Connect Wallet
                  </Button>
                )}
                {firebaseUser ? (
                  <Button
                    className="w-full gap-2 border-red-500/30 text-red-400 hover:bg-red-500/10"
                    variant="outline"
                    onClick={() => { logout(); setMobileOpen(false); }}
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </Button>
                ) : (
                  <Link href="/auth" onClick={() => setMobileOpen(false)}>
                    <Button className="w-full gap-2" variant="outline">
                      <LogIn className="w-4 h-4" />
                      Sign In
                    </Button>
                  </Link>
                )}
              </div>
            </nav>
          </div>
        )}
      </header>

      <WalletPanel open={walletOpen} onClose={() => setWalletOpen(false)} />
    </>
  );
}
